import { useCallback, useLayoutEffect, useState } from 'react';
import { DateTime } from 'luxon';

interface UseScrollSnapProps {
  startTime: DateTime;
  intervalHeight: number;
  scrollTargetSelector?: string; // Defaults to '.right-col'
}

export const useScrollSnap = ({
  startTime,
  intervalHeight,
  scrollTargetSelector = '.right-col',
}: UseScrollSnapProps) => {
  const [hasScrolled, setHasScrolled] = useState(false);

  // Reusable function to scroll to startTime
  const scrollToStartTime = useCallback(
    (targetElement?: HTMLDivElement) => {
      // Convert startTime DateTime to minutes from midnight
      const startTimeMinutes = startTime.hour * 60 + startTime.minute;
      const intervalsFromMidnight = startTimeMinutes / 15; // Each interval is 15 minutes
      const scrollPosition = intervalsFromMidnight * intervalHeight;

      // Find the scroll target element
      const scrollElement =
        (targetElement?.closest(scrollTargetSelector) as HTMLDivElement) ||
        (document.querySelector(scrollTargetSelector) as HTMLDivElement);

      if (scrollElement) {
        scrollElement.scrollTop = scrollPosition;
        setHasScrolled(true);
      }
    },
    [startTime, intervalHeight, hasScrolled, scrollTargetSelector],
  );

  // Callback ref to handle scrolling when the element is mounted
  const scrollRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        scrollToStartTime(node);
      }
    },
    [scrollToStartTime],
  );

  // Fallback to ensure scrolling happens even if callback ref doesn't work
  useLayoutEffect(() => {
    if (hasScrolled) return;
    scrollToStartTime();
  }, [scrollToStartTime, hasScrolled]);

  return {
    scrollRefCallback,
    scrollToStartTime,
    hasScrolled,
  };
};
